import { XData } from '@core/utils';

type FirstCharModifier = (letter: string) => string;

const capitalizeLetter: FirstCharModifier = (letter) => letter.toUpperCase();
const lowercaseLetter: FirstCharModifier = (letter) => letter.toLowerCase();

const defaultShallowModifier = (e: unknown) => e;

const shallowCopyObjLetters = (
  obj: Object,
  firstCharModifier: FirstCharModifier,
  objModifier = defaultShallowModifier
) => {
  if (!obj) return obj;

  return Object.entries(obj).reduce((prevVal, [key, val]) => {
    const modifiedFirstLetterName =
      firstCharModifier(key.charAt(0)) + key.slice(1);

    return { ...prevVal, [modifiedFirstLetterName]: objModifier(val) };
  }, {});
};

const shallowCapitalizeObj = <TResult extends Object>(obj: any) =>
  shallowCopyObjLetters(obj, capitalizeLetter) as TResult;

const shallowLowercaseObj = <TResult extends Object>(obj: any) =>
  shallowCopyObjLetters(obj, lowercaseLetter) as TResult;

const deepCopyObjLetters = (
  item: unknown,
  firstCharModifier: FirstCharModifier
): unknown => {
  if (XData.isObjLiteral(item)) {
    return shallowCopyObjLetters(item as Object, firstCharModifier, (itemVal) =>
      deepCopyObjLetters(itemVal, firstCharModifier)
    );
  }

  const itemInf = item as unknown;

  if (XData.isArray(itemInf)) {
    return itemInf.map((arrItem) =>
      deepCopyObjLetters(arrItem, firstCharModifier)
    );
  }

  return item;
};

const deepCapitalizeObj = <TResult>(obj: TResult): TResult =>
  deepCopyObjLetters(obj, capitalizeLetter) as TResult;

const deepLowercaseObj = <TResult>(obj: TResult): TResult =>
  deepCopyObjLetters(obj, lowercaseLetter) as TResult;

export default {
  shallowCapitalizeObj,
  shallowLowercaseObj,
  deepCapitalizeObj,
  deepLowercaseObj
};
